import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import BrandInquiryForm from '../components/BrandInquiryForm';
import CopyEmailLink from '../components/CopyEmailLink';
import { Compass, BookOpen, Sparkles, Users, Mail, Languages, Shield, MapPin, Eye } from 'lucide-react';

const ForBrandsPage = () => {
  const { t } = useLanguage();

  const valueProps = [
    {
      icon: Compass,
      title_nl: 'Europese focus',
      title_en: 'European focus',
      desc_nl:
        'U&Fashion is opgebouwd rond fashion-lovers in de Benelux en bredere EU — een geëngageerde, koopkrachtige doelgroep met een voorliefde voor stijl.',
      desc_en:
        'U&Fashion is built around fashion-lovers in the Benelux and broader EU — an engaged, purchase-ready audience with a love for style.',
    },
    {
      icon: Sparkles,
      title_nl: 'Doordachte curatie',
      title_en: 'Thoughtful curation',
      desc_nl:
        'Geen oneindige catalogus. We selecteren merken op stijl, kwaliteit en identiteit. Dat geeft jouw merk de aandacht die het verdient.',
      desc_en:
        'No endless catalogue. We select brands on style, quality and identity. That gives your brand the attention it deserves.',
    },
    {
      icon: BookOpen,
      title_nl: 'Redactionele aanpak',
      title_en: 'Editorial approach',
      desc_nl:
        'Magazine-stijl Brand Spotlights, seizoensgebonden verhalen en categorie-context — geen platte productlijstjes.',
      desc_en:
        'Magazine-style Brand Spotlights, seasonal storytelling and category context — not flat product listings.',
    },
    {
      icon: Users,
      title_nl: 'Direct partnerschap',
      title_en: 'Direct partnership',
      desc_nl:
        'Werk rechtstreeks met ons samen voor zichtbaarheid, content, campagnes of een Brand Spotlight — zonder tussenpartij.',
      desc_en:
        'Work directly with us for visibility, content, campaigns or a Brand Spotlight — without middlemen.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="for-brands-page">
      <SEO
        title={t(
          'Voor merken | Partner met U&Fashion',
          'For brands | Partner with U&Fashion'
        )}
        description={t(
          'U&Fashion is een onafhankelijk, gecureerd fashion-platform voor de Benelux en EU. Werk samen met ons via een Brand Spotlight, redactionele zichtbaarheid of een directe partnership.',
          'U&Fashion is an independent, curated fashion platform for the Benelux and EU. Partner with us through a Brand Spotlight, editorial visibility or a direct partnership.'
        )}
        keywords={t(
          'fashion partner, brand spotlight, fashion platform Benelux, merken zichtbaarheid, fashion samenwerking',
          'fashion partner, brand spotlight, fashion platform Benelux, brand visibility, fashion partnership'
        )}
        canonical={
          typeof window !== 'undefined' ? window.location.origin + '/for-brands' : 'https://uandfashion.eu/for-brands'
        }
      />

      {/* Hero — atmospheric full-bleed with editorial photo */}
      <section className="relative h-[78vh] min-h-[560px] max-h-[820px] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_lingua-assist-6/artifacts/w73vnve5_Closeup-96.webp"
          alt={t(
            'Editorial fashion campagne — U&Fashion partnerschap voor merken',
            'Editorial fashion campaign — U&Fashion partnership for brands'
          )}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center 30%' }}
          fetchPriority="high"
          decoding="async"
          width="1920"
          height="1280"
        />
        {/* Soft gradient overlays for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/10" aria-hidden="true" />

        <div className="relative h-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-14 flex flex-col justify-end pb-16 sm:pb-20">
          <p
            className="text-[11px] uppercase tracking-[0.4em] text-white/90 mb-5"
            data-testid="for-brands-eyebrow"
          >
            {t('Voor merken', 'For brands')}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.05] tracking-tight max-w-3xl">
            {t(
              'Bereik de juiste fashion-doelgroep — met verhaal, niet met ruis.',
              'Reach the right fashion audience — with story, not with noise.'
            )}
          </h1>
          <p className="text-base sm:text-lg text-white/85 leading-relaxed font-light max-w-2xl mt-6">
            {t('U&Fashion is een ', 'U&Fashion is an ')}
            <strong className="font-medium text-white">
              {t('onafhankelijk, gecureerd fashion-platform', 'independent, curated fashion platform')}
            </strong>
            {t(' voor Europese merken. We werken met ', ' for European brands. We work with ')}
            <strong className="font-medium text-white">
              {t('affiliate-partnerships', 'affiliate partnerships')}
            </strong>
            {t(' én ', ' and ')}
            <strong className="font-medium text-white">
              {t('directe brand-samenwerkingen', 'direct brand collaborations')}
            </strong>
            {t(
              ' — altijd op basis van doordachte selectie en zorgvuldige presentatie.',
              ' — always grounded in thoughtful selection and careful presentation.'
            )}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <a
              href="#inquiry"
              className="bg-white text-gray-900 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-pink-50 transition-colors inline-flex items-center gap-2 shadow-sm"
              data-testid="for-brands-cta-primary"
            >
              <Mail className="w-4 h-4" />
              {t('Neem contact op', 'Get in touch')}
            </a>
            <a
              href="#how-we-work"
              className="border border-white/40 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors backdrop-blur-sm"
              data-testid="for-brands-cta-secondary"
            >
              {t('Hoe we werken', 'How we work')}
            </a>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20" id="how-we-work">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
            {t('Waarom samenwerken', 'Why work with us')}
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 tracking-tight">
            {t('Vier redenen waarom merken voor U&Fashion kiezen', 'Four reasons brands choose U&Fashion')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {valueProps.map((v, i) => {
            const Icon = v.icon;
            return (
              <div
                key={i}
                className="bg-white border border-gray-100 rounded-2xl p-7 hover:shadow-md transition-shadow"
                data-testid={`for-brands-value-${i}`}
              >
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2.5">
                  {t(v.title_nl, v.title_en)}
                </h3>
                <p className="text-gray-600 leading-relaxed">{t(v.desc_nl, v.desc_en)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What we offer / formats */}
      <section className="bg-gray-50 py-20 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
              {t('Samenwerkingsvormen', 'Partnership formats')}
            </p>
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 tracking-tight">
              {t('Wat we samen kunnen opzetten', 'What we can build together')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-7 border border-gray-100" data-testid="for-brands-format-spotlight">
              <p className="text-[11px] uppercase tracking-[0.25em] text-pink-500 mb-2">
                {t('Editorial', 'Editorial')}
              </p>
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                {t('Brand Spotlight', 'Brand Spotlight')}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                {t(
                  'Een redactioneel verhaal over je merk: stijl, visie, signature collecties. Inclusief een eigen pagina en homepage-feature.',
                  'An editorial story about your brand: style, vision, signature collections. Includes a dedicated page and homepage feature.'
                )}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-gray-100" data-testid="for-brands-format-category">
              <p className="text-[11px] uppercase tracking-[0.25em] text-pink-500 mb-2">
                {t('Zichtbaarheid', 'Visibility')}
              </p>
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                {t('Categorie-curatie', 'Category curation')}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                {t(
                  'Premium plaatsing binnen de juiste rubriek — geselecteerd op stijl-match, met aandacht voor presentatie en context.',
                  'Premium placement within the right category — selected on style match, with care for presentation and context.'
                )}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-gray-100" data-testid="for-brands-format-campaign">
              <p className="text-[11px] uppercase tracking-[0.25em] text-pink-500 mb-2">
                {t('Campagne', 'Campaign')}
              </p>
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                {t('Seizoenscampagne', 'Seasonal campaign')}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                {t(
                  'Een geïntegreerde seizoensfeature: hero-banner, brand story, nieuwsbrief-vermelding en social uitspeling.',
                  'An integrated seasonal feature: hero banner, brand story, newsletter mention and social rollout.'
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust signals — concrete proof points */}
      <section className="border-y border-gray-100 bg-gray-50/60">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Languages, label_nl: 'Tweetalig', sub_nl: 'NL & EN', label_en: 'Bilingual', sub_en: 'NL & EN' },
              { icon: MapPin, label_nl: 'EU-focus', sub_nl: 'Benelux & breder', label_en: 'EU focus', sub_en: 'Benelux & wider' },
              { icon: Eye, label_nl: 'Doordachte selectie', sub_nl: 'Geen open catalogus', label_en: 'Curated selection', sub_en: 'Not an open catalogue' },
              { icon: Shield, label_nl: 'GDPR-conform', sub_nl: 'EU-data, EU-hosting', label_en: 'GDPR-compliant', sub_en: 'EU data, EU hosting' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="text-center" data-testid={`for-brands-trust-${i}`}>
                  <div className="inline-flex w-10 h-10 rounded-full bg-white border border-gray-200 items-center justify-center mb-3">
                    <Icon className="w-4 h-4 text-gray-700" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">{t(item.label_nl, item.label_en)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t(item.sub_nl, item.sub_en)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-20" id="inquiry">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
            {t('Contact', 'Contact')}
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 tracking-tight mb-4">
            {t('Laten we kennismaken', "Let's talk")}
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-xl mx-auto">
            {t(
              'Vertel ons over je merk en wat je wil bereiken. We antwoorden persoonlijk binnen enkele werkdagen.',
              'Tell us about your brand and what you want to achieve. We respond personally within a few business days.'
            )}
          </p>
        </div>
        <BrandInquiryForm />
        <p className="text-center text-sm text-gray-500 mt-8">
          {t('Liever direct mailen? ', 'Prefer to email directly? ')}
          <CopyEmailLink
            email="info@uandfashion.eu"
            className="text-purple-600 underline hover:text-purple-800 cursor-pointer"
            testId="for-brands-direct-email"
          />
        </p>
      </section>
    </div>
  );
};

export default ForBrandsPage;
